import { FC } from "react";

import { getField, getOppositeLocale, Locales } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { FundFormData, FundFormFields } from "./FundFormFields";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import {
  DashboardDialog,
  DashboardDialogBody,
  DashboardDialogContent,
  DashboardDialogFooter,
  DashboardDialogHeader,
} from "@/dashboard-design-system/DashboardDialog";
import { Edit } from "@/icons/outlined";

interface FundFieldData {
  locale: Locales;
  name: string;
}

export interface EditFundData {
  id: string;
  code: string | null;
  fundFields: FundFieldData[];
}

interface EditFundModalProps {
  isOpen: boolean;
  onClose: () => void;
  fund: EditFundData;
}

export const EditFundModal: FC<EditFundModalProps> = ({ isOpen, onClose, fund }) => {
  const { t } = useTranslate();
  const { currentUser, organization } = useCurrentUserContext();
  const locale = currentUser?.locale ?? Locales.EN;
  const utils = trpc.useUtils();

  const secondaryLocale = organization?.country
    ? getOppositeLocale({ organizationCountry: organization.country, locale })
    : undefined;

  const existingTranslation = secondaryLocale ? fund.fundFields?.find((f) => f.locale === secondaryLocale) : undefined;

  const primaryName = getField(fund.fundFields, locale)?.name ?? "";

  const form = useForm<FundFormData>({
    defaultValues: {
      name: primaryName,
      code: fund.code ?? "",
      translatedName: existingTranslation?.name ?? "",
    },
  });

  const { errors } = form.formState;

  const { mutateAsync: updateFund, isPending } = trpc.updateFund.useMutation();

  const handleTranslationRemoved = () => {
    form.setValue("translatedName", "");
  };

  const onSubmit = form.handleSubmit(async (data: FundFormData) => {
    const translatedName = data.translatedName || null;

    const { error } = await updateFund({
      fundId: fund.id,
      name: data.name,
      code: data.code || null,
      translatedName,
    });
    if (error) {
      enqueueSnackbar(t("dashboard", "funds.edit.error"), { vibe: "danger" });
    } else {
      enqueueSnackbar(t("dashboard", "funds.edit.success"), { vibe: "positive" });
      await utils.getFundsByOrganizationId.invalidate();
      onClose();
    }
  });

  return (
    <DashboardDialog open={isOpen} onClose={onClose} maxWidth="xs">
      <DashboardDialogBody
        slots={{
          header: <DashboardDialogHeader icon={<Edit />} title={t("dashboard", "funds.edit.title")} />,
          content: (
            <DashboardDialogContent>
              <form noValidate>
                <FundFormFields
                  control={form.control}
                  errors={errors}
                  description={t("dashboard", "funds.edit.description")}
                  locale={locale}
                  organizationCountry={organization?.country}
                  initialShowTranslation={Boolean(existingTranslation)}
                  onTranslationRemoved={handleTranslationRemoved}
                />
              </form>
            </DashboardDialogContent>
          ),
          footer: (
            <DashboardDialogFooter>
              <DashboardButton onClick={onSubmit} isLoading={isPending}>
                {t("dashboard", "funds.edit.saveChanges")}
              </DashboardButton>
              <DashboardButton onClick={onClose}>{t("dashboard", "common.cancel")}</DashboardButton>
            </DashboardDialogFooter>
          ),
        }}
      />
    </DashboardDialog>
  );
};
